import { useState, useEffect, useCallback } from 'react';
import { X, Sparkles, CheckCircle, Crown, ArrowLeft } from 'lucide-react';
import CompleteEnhancedResumeBuilder from './CompleteEnhancedResumeBuilder';
import ResumeErrorBoundary from './ResumeErrorBoundary';
import { useAuth } from '../contexts/AuthContext';
import { useResumeAI } from '../hooks/useResumeAI';
import { JobTargetFlow } from './resume/ui/JobTargetFlow';

// ─── Coming Soon Modal ───────────────────────────────────────────────────────
const ComingSoonModal = ({ onClose }) => (
  <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[9999] flex items-center justify-center p-4">
    <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 text-center relative">
      <button
        onClick={onClose}
        className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
        aria-label="Close"
      >
        <X className="w-5 h-5" />
      </button>
      <div className="w-16 h-16 bg-gradient-to-br from-purple-600 to-pink-600 rounded-2xl flex items-center justify-center mx-auto mb-5">
        <Sparkles className="w-8 h-8 text-white" />
      </div>
      <h3 className="text-xl font-bold text-gray-900 mb-2">AI Resume Builder</h3>
      <p className="text-purple-600 font-semibold mb-4">Coming Soon</p>
      <p className="text-gray-500 text-sm mb-6 leading-relaxed">
        We&apos;re building the most advanced AI-powered QA resume builder. Intelligent content generation,
        ATS optimisation, and personalised recommendations — launching soon.
      </p>
      <button
        onClick={onClose}
        className="w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white py-3 rounded-xl font-semibold hover:from-purple-700 hover:to-pink-700 transition-all"
      >
        Got it
      </button>
    </div>
  </div>
);

// ─── Pre-launch Job Target Modal has been moved to JobTargetFlow.jsx ───────────

// ─── Selection Screen ────────────────────────────────────────────────────────
const SelectionScreen = ({ onSelectBuilder, onClose }) => {
  const [showComingSoon, setShowComingSoon] = useState(false);

  return (
    <div className="fixed inset-0 bg-white flex flex-col z-[7999]" style={{ top: '64px' }}>
      {/* Sticky header with back button */}
      <div className="sticky top-0 z-10 bg-white border-b border-gray-100 px-4 sm:px-6 py-2.5 sm:py-3 flex-shrink-0">
        <button
          onClick={onClose}
          className="flex items-center gap-2 px-3 py-1.5 sm:py-2 bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-lg transition-all text-xs sm:text-sm font-medium text-gray-700 active:scale-95"
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </button>
      </div>

      <div className="flex-1 min-h-0 bg-gradient-to-br from-gray-50 to-blue-50 overflow-y-auto overflow-x-hidden scroll-touch">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-12">
          {/* Hero */}
          <div className="text-center mb-6 sm:mb-12">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-2 sm:mb-3 leading-tight tracking-tight">
              Build Your QA Resume
            </h2>
            <p className="text-xs sm:text-base md:text-lg text-gray-500 max-w-xl mx-auto leading-relaxed">
              ATS-optimised, recruiter-ready, built for QA professionals
            </p>
          </div>

          {/* Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 sm:gap-6 max-w-3xl mx-auto">
            {/* AI Builder — Coming Soon */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-4 sm:p-6 md:p-7 flex flex-col justify-between">
              <div>
                <div className="flex items-center gap-2.5 sm:gap-3 mb-4 sm:mb-5">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-purple-600 to-pink-600 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Sparkles className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="text-base sm:text-lg font-bold text-gray-900 leading-tight">AI Resume Builder</h3>
                    <span className="inline-block mt-0.5 text-[10px] sm:text-xs font-semibold text-purple-600 bg-purple-50 px-2 py-0.5 rounded-full">Coming Soon</span>
                  </div>
                </div>
                <ul className="space-y-1.5 sm:space-y-2 mb-5 sm:mb-6">
                  {['Paste job description → tailored resume', 'AI content generation', 'ATS keyword optimisation', 'Unlimited generations'].map(f => (
                    <li key={f} className="flex items-start gap-2 text-xs sm:text-sm text-gray-600 leading-snug sm:leading-normal">
                      <CheckCircle className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-green-400 flex-shrink-0 mt-0.5" />
                      <span className="min-w-0 flex-1">{f}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <button
                onClick={() => setShowComingSoon(true)}
                className="w-full bg-gray-100 text-gray-400 py-2.5 sm:py-3 rounded-xl font-semibold text-xs sm:text-sm cursor-not-allowed min-h-[42px] flex items-center justify-center"
                disabled
              >
                Coming Soon
              </button>
            </div>

            {/* QA Builder — Free, no auth required */}
            <div className="bg-white rounded-2xl shadow-lg border-2 border-blue-600 p-4 sm:p-6 md:p-7 flex flex-col justify-between relative mt-2 sm:mt-0">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-10">
                <span className="bg-blue-600 text-white text-[10px] sm:text-xs font-bold px-2.5 sm:px-3 py-0.5 sm:py-1 rounded-full shadow-sm uppercase tracking-wider whitespace-nowrap">FREE</span>
              </div>
              <div>
                <div className="flex items-center gap-2.5 sm:gap-3 mb-4 sm:mb-5">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-blue-600 to-blue-700 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Crown className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="text-base sm:text-lg font-bold text-gray-900 leading-tight">QA Resume Builder</h3>
                    <p className="text-[11px] sm:text-xs text-blue-600 font-medium">For QA Professionals</p>
                  </div>
                </div>
                <ul className="space-y-1.5 sm:space-y-2 mb-5 sm:mb-6">
                  {[
                    'ATS-safe PDF download — instant',
                    'AI QA tools & 2026 skills',
                    'Live ATS score feedback',
                    'Auto-save — never lose progress',
                    'ISTQB, Selenium, Playwright & more',
                  ].map(f => (
                    <li key={f} className="flex items-start gap-2 text-xs sm:text-sm text-gray-600 leading-snug sm:leading-normal">
                      <CheckCircle className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="min-w-0 flex-1">{f}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <button
                onClick={() => onSelectBuilder('qa-builder')}
                className="w-full bg-gradient-to-r from-blue-600 to-blue-700 text-white py-2.5 sm:py-3 px-3 sm:px-4 rounded-xl font-bold text-xs sm:text-sm hover:from-blue-700 hover:to-blue-800 transition-all shadow-md flex items-center justify-center gap-1.5 sm:gap-2 active:scale-98 min-h-[42px]"
              >
                <Crown className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0" />
                <span>Start Building — Free</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {showComingSoon && <ComingSoonModal onClose={() => setShowComingSoon(false)} />}
    </div>
  );
};

// ─── Main Router ─────────────────────────────────────────────────────────────
const ResumeBuilderRouter = ({ isOpen, onClose }) => {
  const [currentPage, setCurrentPage] = useState('selection');
  const [userEmail, setUserEmail]     = useState('');
  const [showPreLaunchJD, setShowPreLaunchJD]     = useState(false);
  const [pendingJD, setPendingJD]                 = useState('');
  const [pendingKeywords, setPendingKeywords]     = useState([]);
  const { user, loading }             = useAuth();
  const ai                            = useResumeAI();


  // Lock background page scroll while builder is open & notify floating widgets
  useEffect(() => {
    if (!isOpen) return;
    window.dispatchEvent(new CustomEvent('open-resume-builder'));
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
      window.dispatchEvent(new CustomEvent('close-resume-builder'));
    };
  }, [isOpen]);

  // Sync email from auth (non-blocking)
  useEffect(() => {
    if (user?.email) setUserEmail(user.email);
  }, [user]);

  // Listen for custom navigation events
  useEffect(() => {
    const handleNavigateToQA = (e) => {
      if (e?.detail?.userEmail) setUserEmail(e.detail.userEmail);
      setCurrentPage('qa-builder');
    };
    const handleNavAway = () => { onClose(); };

    window.addEventListener('navigate-to-qa-builder', handleNavigateToQA);
    window.addEventListener('navigate', handleNavAway);
    window.addEventListener('popstate', handleNavAway);
    window.addEventListener('close-resume-builder', handleNavAway);
    return () => {
      window.removeEventListener('navigate-to-qa-builder', handleNavigateToQA);
      window.removeEventListener('navigate', handleNavAway);
      window.removeEventListener('popstate', handleNavAway);
      window.removeEventListener('close-resume-builder', handleNavAway);
    };
  }, [onClose]);

  const handleSelectBuilder = useCallback((type) => {
    // Show the pre-launch JD modal before entering the builder
    if (type === 'qa-builder') {
      if (localStorage.getItem('ai_limit_reached') === 'true') {
        setCurrentPage('qa-builder');
      } else {
        setShowPreLaunchJD(true);
      }
    } else {
      setCurrentPage(type);
    }
  }, []);

  const handleJDStart = useCallback((jdText, approvedKeywords = []) => {
    setPendingJD(jdText || '');
    setPendingKeywords(approvedKeywords || []);
    setShowPreLaunchJD(false);
    setCurrentPage('qa-builder');
  }, []);

  const handleJDSkip = useCallback(() => {
    setPendingJD('');
    setShowPreLaunchJD(false);
    setCurrentPage('qa-builder');
  }, []);

  const handleBack = useCallback(() => {
    setCurrentPage('selection');
  }, []);

  const handleClose = useCallback(() => {
    setCurrentPage('selection');
    onClose();
  }, [onClose]);

  if (!isOpen) return null;

  // Auth loading — show minimal spinner, don't block builder access
  if (loading) {
    return (
      <div className="fixed inset-0 bg-white z-[7999] flex items-center justify-center" style={{ top: '64px' }}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-4 border-blue-600 border-t-transparent mx-auto mb-3" />
          <p className="text-gray-500 text-sm">Loading...</p>
        </div>
      </div>
    );
  }

  // QA Builder — no auth gate
  if (currentPage === 'qa-builder') {
    return (
      <ResumeErrorBoundary onClose={handleClose}>
        <div className="fixed inset-0 bg-white overflow-y-auto" style={{ top: '64px', zIndex: 8000 }}>
          <CompleteEnhancedResumeBuilder
            onBack={handleBack}
            userEmail={userEmail}
            initialJobDescription={pendingJD}
            initialKeywordsToInject={pendingKeywords}
          />
        </div>
      </ResumeErrorBoundary>
    );
  }

  // Selection screen (default) + Pre-launch JD modal overlay
  return (
    <ResumeErrorBoundary onClose={handleClose}>
      <SelectionScreen onSelectBuilder={handleSelectBuilder} onClose={handleClose} />
      {showPreLaunchJD && (
        <JobTargetFlow
          isOpen={true}
          onClose={handleJDSkip}
          onComplete={(jdText, approved, analysis, startFresh) => {
            if (startFresh) {
              localStorage.removeItem('qa_resume_builder_v3');
            }
            handleJDStart(jdText, approved, analysis);
          }}
          analyzeJobDescriptionAsync={ai.analyzeJobDescription}
          mode="pre-launch"
        />
      )}
    </ResumeErrorBoundary>
  );
};

export default ResumeBuilderRouter;
